// Regras puras das permissões do modal de usuário: agrupamento do mockup,
// leitura/escrita do valor de cada permissão e contagem de ativas.
// Sem React — testado em __tests__/permissionRules.test.js.
//
// ATENÇÃO: o valor gravado NÃO é uniforme entre os campos, e essa herança
// não foi mexida aqui para não quebrar o backend:
//   - `allTicket` usa "enable"/"disable" (sem o "d")
//   - `allowGroup` é booleano de verdade
//   - `finalizacaoComValorVendaAtiva` é a STRING "true"/"false"
//   - os demais usam "enabled"/"disabled"
// Errar isso desliga a permissão em silêncio, por isso mora numa regra pura.
import { i18n } from "../../translate/i18n";

const ENABLED = { on: "enabled", off: "disabled" };
const ENABLE = { on: "enable", off: "disable" };
const BOOL = { on: true, off: false };
const BOOL_STRING = { on: "true", off: "false" };

const perm = (name, icon, labelKey, encoding = ENABLED) => ({
  name,
  icon,
  labelKey,
  encoding,
});

export const PERMISSION_GROUPS = Object.freeze([
  {
    titleKey: "userModal.permissionGroups.tickets",
    items: [
      perm("allTicket", "inbox", "userModal.permissions.allTicket", ENABLE),
      perm("allowGroup", "groups", "userModal.permissions.allowGroup", BOOL),
      perm("allHistoric", "history", "userModal.permissions.allHistoric"),
      perm(
        "allowSeeMessagesInPendingTickets",
        "visibility",
        "userModal.permissions.allowSeeMessagesInPendingTickets"
      ),
      perm(
        "userClosePendingTicket",
        "taskAlt",
        "userModal.permissions.userClosePendingTicket"
      ),
      perm(
        "finalizacaoComValorVendaAtiva",
        "paid",
        "userModal.permissions.finalizacaoComValorVendaAtiva",
        BOOL_STRING
      ),
    ],
  },
  {
    titleKey: "userModal.permissionGroups.modules",
    items: [
      perm("showDashboard", "dashboard", "userModal.permissions.showDashboard"),
      perm("showContacts", "contacts", "userModal.permissions.showContacts"),
      perm("showCampaign", "campaign", "userModal.permissions.showCampaign"),
      perm("showFlow", "flow", "userModal.permissions.showFlow"),
      perm("allowConnections", "connections", "userModal.permissions.allowConnections"),
      perm("allowRealTime", "realTime", "userModal.permissions.allowRealTime"),
    ],
  },
  {
    titleKey: "userModal.permissionGroups.internal",
    items: [perm("allUserChat", "forum", "userModal.permissions.allUserChat")],
  },
]);

const ALL_ITEMS = PERMISSION_GROUPS.reduce(
  (acc, group) => acc.concat(group.items),
  []
);

export const permissionMeta = (name) =>
  ALL_ITEMS.find((item) => item.name === name) || null;

// Aceita qualquer forma "ligada" que o backend já tenha gravado ao longo do
// tempo — `true`, "true", "enable" e "enabled" — para um registro antigo não
// aparecer desligado por causa da grafia.
export const isPermissionOn = (name, value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  const normalized = String(value).toLowerCase().trim();
  return normalized === "true" || normalized === "enable" || normalized === "enabled";
};

// Valor a gravar no campo, respeitando o encoding dele.
export const permissionValue = (name, on) => {
  const meta = permissionMeta(name);
  const encoding = meta ? meta.encoding : ENABLED;
  return on ? encoding.on : encoding.off;
};

// Grupos prontos para a tela, com rótulos traduzidos e o estado de cada item.
export const permissionGroups = (values = {}) =>
  PERMISSION_GROUPS.map((group) => ({
    title: i18n.t(group.titleKey),
    items: group.items.map((item) => ({
      ...item,
      label: i18n.t(item.labelKey),
      on: isPermissionOn(item.name, values[item.name]),
    })),
  }));

// "14 de 15 permissões ativas" do rodapé.
export const countActivePermissions = (values = {}) => ({
  active: ALL_ITEMS.filter((item) => isPermissionOn(item.name, values[item.name]))
    .length,
  total: ALL_ITEMS.length,
});
